/**
 * Prize Pop campaign-specific rules sections
 * Shows prize pop rules, terms, and participation guidelines
 * Rendered by the shared RulesTab shell
 */

import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PRIZE_POP_CAMPAIGN_TYPE,
  useAutoJoin,
  usePrizesCount,
  type IPrizePopCampaign,
  type PrizePopIntervalTimeRange,
} from 'features/campaigns';
import TransWithFallback from 'shared/components/Translations/TransWrapper';
import { formatDate, formatTime } from 'shared/utils/dateHelpers';
import { formatCurrencyValue, formatNumberWithSpaces } from 'shared/utils/helpers';
import type { IRulesContentProps } from '../../../../../shared/tabs/RulesTab/RulesTab';

import styles from './PrizePopRulesContent.module.css';

export const PrizePopRulesContent: FC<IRulesContentProps> = ({ campaign }) => {
  const prizePop = campaign as IPrizePopCampaign;
  const prizesCount = usePrizesCount();
  const autoJoin = useAutoJoin(PRIZE_POP_CAMPAIGN_TYPE);
  const { t } = useTranslation();

  // Group intervals by date range
  const groupedIntervals = prizePop.intervals.reduce(
    (acc, interval) => {
      const startDate = formatDate(interval.range.startRangeDate);
      const endDate = formatDate(interval.range.endRangeDate);
      const fromCapitalized = t('campaigns.from', 'from')[0].toUpperCase() + t('campaigns.from', 'from').slice(1);
      const key =
        startDate === endDate ? startDate : `${fromCapitalized} ${startDate} ${t('campaigns.to', 'to')} ${endDate}`;

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(...interval.hoursRange);
      return acc;
    },
    {} as Record<string, PrizePopIntervalTimeRange[]>,
  );

  return (
    <>
      {/* General Information */}
      <div className={styles.section}>
        <div className={styles.title}>{t('campaigns.general_information', 'General Information')}</div>
        <div>
          <div>
            {t('campaigns.total_prize_pool', 'Total Prize Pool')}:{' '}
            <span className={styles.highlightedText}>
              {formatNumberWithSpaces(prizePop.prizeFund)} {prizePop.baseCurrency}.
            </span>
          </div>
          <div>
            {t('campaigns.total_number_of_prizes', 'Total number of prizes')}:{' '}
            <span className={styles.highlightedText}>{formatNumberWithSpaces(prizesCount.total)}.</span>
          </div>
        </div>
      </div>

      {/* Participation Rules */}
      <div className={styles.section}>
        <div className={styles.title}>{t('campaigns.participation_rules', 'Participation Rules')}</div>
        <div>
          {!autoJoin && (
            <div>
              <TransWithFallback
                i18nKey="campaigns.click_join_in_button"
                components={{ b: <span className={styles.highlightedText} /> }}
              />
            </div>
          )}
          {prizePop.minStake != null && (
            <div>
              {t('campaigns.minimum_stake_amount', 'Minimum stake amount is')}:{' '}
              <span className={styles.highlightedText}>
                {formatCurrencyValue(prizePop.minStake, 100)} {prizePop.baseCurrency}.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Time Intervals */}
      {prizePop.intervals && prizePop.intervals.length > 0 && (
        <div className={styles.section}>
          {Object.entries(groupedIntervals).map(([dateRange, timeSlots], index) => (
            <div key={index}>
              <div className={styles.title}>{dateRange}</div>
              <ul className={styles.timeSlotList}>
                {timeSlots.map((slot, slotIndex) => (
                  <li key={slotIndex} className={styles.timeSlotItem}>
                    {t('campaigns.from', 'from')}{' '}
                    <span className={styles.highlightedText}>{formatTime(slot.startTime)}</span>{' '}
                    {t('campaigns.to', 'to')} <span className={styles.highlightedText}>{formatTime(slot.endTime)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
